import {requestComplete, requestFailed, requestPending, requestUnsent} from "./networkActions";
import {API_ROOT, V1} from "../../constant/apiConstants";
import {LOGIN_USER, LOGOUT_USER} from "../../constant/reducerConstants";
import {DEVICES_PATH, LOGIN_PATH} from "../../constant/routes";
import {push} from 'react-router-redux';
import {errorListClear, errorListPush} from "./alertActions";
import {getCsrfToken, saveCsrfToken} from "../../util/CsrfUtil";
import {AppError} from "../../constant/appAlerts";

/**
 * Get function for API with network status updating. If no callback is provided for successful responses an action is
 * dispatched to the reducers with the type of the request name.
 *
 * @param path - required
 * @param requestName - required - the name of the request to store in the network reducer
 * @param successCallback - optional
 * @param successCode - optional - will default to 200
 * @returns {function(*=): Promise<Response>}
 */
export const get = ({
                        path,
                        requestName,
                        successCallback,
                        successCode = 200
                    }) => dispatch => {
    dispatch(requestPending(requestName));
    return fetch(API_ROOT + path, {credentials: "same-origin"})
        .then(response => {
            if (response.status === 401) {
                dispatch(requestFailed(requestName));
                dispatch(errorListPush(AppError.LOGIN_EXPIRED));
                return null;
            } else if (response.status !== successCode) {
                dispatch(requestFailed(requestName));
                dispatch(errorListPush(AppError.API_FAILURE));
                return null;
            }
            response.json().then(data => {
                dispatch(requestComplete(requestName));
                successCallback ? successCallback(data) : dispatch({type: requestName, data});
            }).catch(error => {
                dispatch(requestFailed(requestName));
                console.error('GET API parse failed for ' + requestName, error);
                return null;
            });
        }).catch(error => {
            dispatch(requestFailed(requestName));
            dispatch(errorListPush(AppError.API_FAILURE));
            console.error('GET API failed for ' + requestName, error);
        });
};

/**
 * POST function for API with network status updating. If no callback is provided for successful responses an action is
 * dispatched to the reducers with the type of the request name.
 *
 * @param body
 * @param path
 * @param requestName - required - the name of the request to store in the network reducer
 * @param successCallback - optional
 * @param successCode - optional - will default to 201
 * @returns {function(*): Promise<Response>}
 */
export const post = ({
                         body,
                         path,
                         requestName,
                         successCallback,
                         successCode = 201
                     }) => dispatch => {
    dispatch(requestPending(requestName));
    const {csrfHeader, csrfToken} = getCsrfToken();
    return fetch(API_ROOT + path, {
        credentials: "same-origin",
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
            [csrfHeader]: csrfToken
        },
        body: JSON.stringify(body)
    }).then(response => {
        if (response.status === 401) {
            dispatch(requestFailed(requestName));
            dispatch(errorListPush(AppError.LOGIN_EXPIRED));
            return null;
        } else if (response.status !== successCode) {
            dispatch(requestFailed(requestName));
            dispatch(errorListPush(AppError.API_FAILURE));
            return null;
        }
        response.json().then(data => {
            dispatch(requestComplete(requestName));
            successCallback ? successCallback(data)(dispatch) : dispatch({type: requestName, data});
        }).catch(error => {
            dispatch(requestFailed(requestName));
            console.error('GET API parse failed for ' + requestName, error);
            return null;
        });
    }).catch(error => {
        dispatch(requestFailed(requestName));
        console.error('GET API failed for ' + requestName, error);
    });
};

/**
 * Function to pass authorization to server to authenticate the session.
 *
 * @param siteId - required
 * @param taskId - required
 * @param pin - required
 * @returns {function(*=): Promise<Response>}
 */
export const tryLogin = ({taskId, pin}) => dispatch => {
    const token = btoa(taskId + ':' + pin);
    const options = {
        throwError: true,
        credentials: 'same-origin',
        headers: {Authorization: 'Basic ' + token}
    };
    dispatch(login({options}));
};

/**
 * Function to check session cookie against server
 *
 * @returns {Function}
 */
export const checkLogin = dispatch => {
    const options = {
        throwError: false,
        credentials: 'same-origin'
    };
    dispatch(login({options}));
};

const login = ({options}) => dispatch => {
    dispatch(requestPending(LOGIN_USER));
    return fetch(API_ROOT + V1 + '/user/', options)
        .then(response => {
            saveCsrfToken(response);
            if (response.status !== 200) {
                dispatch(requestFailed(LOGIN_USER));
                options.throwError && dispatch(errorListPush(AppError.LOGIN_FAILED));
                return null;
            }
            response.json().then(data => {
                dispatch(errorListClear());
                dispatch(requestUnsent(LOGOUT_USER));
                dispatch(requestComplete(LOGIN_USER));
                dispatch({type: LOGIN_USER, data});
                dispatch(push(DEVICES_PATH));
            }).catch(error => {
                dispatch(requestFailed(LOGIN_USER));
                console.error('GET API parse failed for ' + LOGIN_USER, error);
                return null;
            });
        }).catch(error => {
            dispatch(requestFailed(LOGIN_USER));
            console.error('GET API failed for ' + LOGIN_USER, error);
        });
};

export const logout = dispatch => {
    dispatch(requestPending(LOGOUT_USER));
    dispatch(push(LOGIN_PATH));
    return fetch(API_ROOT + V1 + '/user/logout', {credentials: 'same-origin'})
        .then(response => {
            if (response.status !== 200) {
                dispatch(requestFailed(LOGOUT_USER));
            } else {
                dispatch(requestComplete(LOGOUT_USER));
            }
            dispatch(requestUnsent(LOGIN_USER));
            dispatch({type: LOGOUT_USER});
            dispatch(errorListClear());
        }).catch(error => {
            dispatch(requestFailed(LOGOUT_USER));
        });
};




// WEBPACK FOOTER //
// ./src/state/action/apiActions.js